import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';

/**
 * A file ANNOUNCED before it exists: the agent attaches this while a design is being generated, so the
 * reply shows the files arriving, and replaces it with the real asset when that lands (the harness's
 * AttachFile, through the `attachments` mask of UpdateConversationMessage). It carries a name and a type
 * and NEITHER address, which is what identifies it — there is no flag for anything to disagree with.
 *
 * The swap reaches an open chat on the ASSISTANT_CHAT push that already delivers attachments to a
 * streamed bubble (`patchAttachmentsById`), so nothing here has to poll or refetch.
 *
 * Kept as its own module rather than shared from common-lib, and duplicated in the harness and both
 * phones, the way `file-record` is: it is two comparisons, and each surface expresses them in its own
 * language anyway.
 */
export function isPlaceholderAttachment(attachment: Pick<MessageAttachment, 'url' | 'vfsPath'> & { groupFile?: MessageAttachment['groupFile'] }): boolean {
  // A file shared into a group (Phase 7) is addressed by its `groupFile` reference, not by either address.
  return !attachment.url?.trim() && !attachment.vfsPath?.trim() && !attachment.groupFile?.fileId;
}

/** Beyond this, a picture that never arrived reads as stuck rather than merely slow — a real render lands
 *  within a couple of minutes even on a heavy edit, and nothing here can tell "still working" from "the
 *  agent gave up" any more precisely than that. */
export const STALE_PLACEHOLDER_MS = 10 * 60_000;

/** Whether a placeholder created at `createdAt` (the row's own timestamp — never a client guess) has been
 *  waiting long enough to treat as abandoned. */
export function isStalePlaceholder(createdAt: string | undefined, now: number = Date.now()): boolean {
  if (!createdAt) return false;
  const created = Date.parse(createdAt);
  return Number.isFinite(created) && now - created > STALE_PLACEHOLDER_MS;
}

/** Beyond this, a placeholder is a file that is not coming: it stops shimmering and says so. Shared constant-for-constant
 *  with iOS (`AttachmentPlaceholderRules`) and Android (`AttachmentPlaceholder.kt`). The server clears or fills a
 *  placeholder once its brief has ended and the run that might attach it is over (the harness's placeholder sweep), but a
 *  row that sweep never reached (a sub-agent run that outlived its checks, 2026-10-29) would otherwise shimmer forever.
 *  Derived from the row's own timestamp, never latched: a fill that lands later still turns it into the file. */
export const ABANDONED_PLACEHOLDER_MS = 30 * 60_000;

/** Whether a placeholder created at `createdAt` (the row's own timestamp — never a client guess) is a file that is not
 *  coming. No timestamp never abandons: a just-streamed row has none yet, and it is the one most likely to fill. */
export function isAbandonedPlaceholder(createdAt: string | undefined, now: number = Date.now()): boolean {
  if (!createdAt) return false;
  const created = Date.parse(createdAt);
  return Number.isFinite(created) && now - created > ABANDONED_PLACEHOLDER_MS;
}
